import { ChangeEvent, useCallback } from 'react';
import { FormField } from '@nimbus-ds/patterns';
import { ValueRestrictionType } from '@tiendanube/common/src/enums';
import { InputNumberNimbus, Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useUpsellProtectedActions } from '../../../../../hooks';
import OptionsSection from '../../../OptionsSection';
import { RadioOption } from '../../../shared/types';

interface ValueRestrictionSectionProps {
  cartValue: ValueRestrictionType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  cartValueMin?: string;
  cartValueMax?: string;
  currencySymbol: string;
  errors?: Partial<{
    cartValueMin: string;
    cartValueMax: string;
  }>;
}

function ValueRestrictionSection({
  cartValue,
  onChange,
  cartValueMin,
  cartValueMax,
  currencySymbol,
  errors = {},
}: Readonly<ValueRestrictionSectionProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions',
  );

  const { withUpsellCheck } = useUpsellProtectedActions();

  const cartValueOptions: RadioOption[] = [
    {
      label: t('cartValue.allValues'),
      value: ValueRestrictionType.ALL_VALUES,
    },
    {
      label: t('cartValue.valueRange'),
      value: ValueRestrictionType.VALUE_RANGE,
    },
  ];

  const handleChange = withUpsellCheck(
    useCallback((e: ChangeEvent<HTMLInputElement>) => onChange(e), [onChange]),
  );

  return (
    <Stack column align="stretch">
      <Stack.Item>
        <OptionsSection
          title={t('cartValue.title')}
          name="valueRestrictionType"
          options={cartValueOptions}
          value={cartValue}
          onChange={handleChange}
        />
      </Stack.Item>

      {cartValue === ValueRestrictionType.VALUE_RANGE && (
        <Stack.Item>
          <Stack wrap align="stretch">
            <Stack.Item fill>
              <FormField
                label={t('cartValue.from')}
                showHelpText
                helpText={
                  errors.cartValueMin ? t(errors.cartValueMin) : undefined
                }
              >
                <InputNumberNimbus
                  type="float"
                  name="cartValueMin"
                  value={cartValueMin ?? ''}
                  onChange={onChange}
                  append={currencySymbol}
                  appearance={errors.cartValueMin ? 'danger' : 'neutral'}
                />
              </FormField>
            </Stack.Item>

            <Stack.Item fill>
              <FormField
                label={t('cartValue.to')}
                showHelpText
                helpText={
                  errors.cartValueMax ? t(errors.cartValueMax) : undefined
                }
              >
                <InputNumberNimbus
                  type="float"
                  name="cartValueMax"
                  value={cartValueMax ?? ''}
                  onChange={onChange}
                  append={currencySymbol}
                  appearance={errors.cartValueMax ? 'danger' : 'neutral'}
                />
              </FormField>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      )}
    </Stack>
  );
}

export default ValueRestrictionSection;
